import { MetricName } from "@iventis/domain-model/model/metricName";
import { MappingEngine, MapEventTypes } from "@iventis/map-engine";
import { incrementMonitoringCount } from "@iventis/observability-and-monitoring";
import { toast } from "@iventis/toasts/src/toast";
import { Content } from "@iventis/translations";
import { translate } from "@iventis/translations/translation";
import { copyImageToClipboard, downloadImage } from "@iventis/utilities";
import { useSelector } from "react-redux";
import { eventStream } from "../event-stream";
import { RootState, dispatch } from "../state/root.store";
import { useMapLoading } from "./use-map-loading";
import { map } from "../state/map.slice";

/** Handles taking screenshots for a map */
export function useMapScreenshot({ allowScreenshot }: { allowScreenshot: () => boolean }) {
    const { userConnectedToDigitalTwin, mappingEngine, mapId } = useSelector((rootState: RootState) => ({
        userConnectedToDigitalTwin: rootState.mapReducer.mapModule.digitalTwinEngineData.userConnectedToDigitalTwin,
        mappingEngine: rootState.mapReducer.mappingEngine,
        mapId: rootState.mapReducer.mapModule.mapId,
    }));

    const isMapStateLoading = useMapLoading();
    const isMapLoadingOrConnecting = mappingEngine === MappingEngine.Mapbox ? isMapStateLoading : !userConnectedToDigitalTwin;

    const takeScreenshot = () => {
        // Do not take screenshot if the user is still connecting
        if (isMapLoadingOrConnecting || !allowScreenshot()) {
            return;
        }

        if (mappingEngine === MappingEngine.Mapbox) {
            incrementMonitoringCount({ name: MetricName.PLANNER_SCREENSHOT, quantity: 1, resourceId: mapId });
        } else {
            incrementMonitoringCount({ name: MetricName.DIGITAL_TWIN_SCREENSHOT, quantity: 1, resourceId: mapId });
        }

        dispatch(map.actions.setTakingScreenshot(true));

        /** This runs if the copy is sucessful */
        const copySuccess = () => {
            dispatch(map.actions.setTakingScreenshot(false));
            toast.success({
                title: translate(Content.map10.screenshot.copiedToClipboardToast.title),
                message: translate(Content.map10.screenshot.copiedToClipboardToast.message),
                testId: "map-screenshot-copied-to-clipboard",
            });
        };

        /** This runs if the copy fails */
        const copyFailure = (dataUrl: string | null) => {
            dispatch(map.actions.setTakingScreenshot(false));
            if (dataUrl === null) {
                toast.error({
                    title: translate(Content.map10.screenshot.errorToast.title),
                    message: translate(Content.map10.screenshot.errorToast.message),
                    testId: "map-screenshot-error",
                });
                return;
            }

            try {
                // Download the image instead if we cant copy to the clipboard
                downloadImage(dataUrl, `${translate(Content.map10.screenshot.screenshot)}.png`);
                toast.success({
                    title: translate(Content.map10.screenshot.downloadedImageToast.title),
                    message: translate(Content.map10.screenshot.downloadedImageToast.message),
                    testId: "map-screenshot-downloaded",
                });
            } catch {
                toast.error({
                    title: translate(Content.map10.screenshot.errorToast.title),
                    message: translate(Content.map10.screenshot.errorToast.message),
                    testId: "map-screenshot-error",
                });
            }
        };

        eventStream.next({
            type: MapEventTypes.TAKE_SCREENSHOT,
            payload: (getImageScreenshot) => {
                copyImageToClipboard(getImageScreenshot, copySuccess, copyFailure);
            },
        });
    };

    return { takeScreenshot };
}
